// "Ver como rol": deja previsualizar el sistema como otro rol (menús, rutas y pestañas se comportan
// como ese rol) para comprobar exactamente qué ve cada uno. No da ni quita poder real: el JWT no cambia.
//   · <VerComoSelector/> — el desplegable para ENTRAR (va en la cabecera del panel de administración).
//   · <VerComoBanner/>    — la barra flotante mientras se previsualiza (cambiar de rol / volver a admin).
//
// B-1000 (1-oct-2026) · ES UNA HERRAMIENTA DE PRUEBAS, Y SOLO LA VE QUIEN PRUEBA. El dueño: «El botón
// "Ver como" solo debe salir a los superadmin: es un botón de pruebas». Hasta hoy le salía a cualquier
// administrador de una clínica. Los dos componentes cuelgan ahora de `puedeVerComo` (AuthContext), que
// es la respuesta del SERVIDOR sobre la cuenta REAL —del equipo de Biango y administradora de esta
// clínica—, la misma con la que el servidor acepta o ignora la previsualización. Al administrador de
// una clínica no se le pinta nada, ni se piden por él los roles ni las personas.
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../context/AuthContext'
import api from '../services/api'
import { Ico } from './ui'
import { t, tx, marca, tDato } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// B-740 · el rol de serie `colaborador` se llama «Colaborador», a secas. t566 · Doctor/a e Higienista, de fábrica desde el 30-oct-2026.
const LABEL_SERIE = { recepcion: marca('components.VerComoRol.recepcion', 'Recepción'), doctor: 'Doctor/a', higienista: marca('components.VerComoRol.higienista', 'Higienista'), colaborador: marca('components.VerComoRol.colaborador', 'Colaborador'), auxiliar: marca('components.VerComoRol.auxiliar', 'Auxiliar'), almacen: marca('components.VerComoRol.almacen', 'Almacén'), limpieza: marca('components.VerComoRol.limpieza', 'Limpieza') }
const nombreRol = (r) => r?.nombre || tDato(LABEL_SERIE[r?.clave]) || r?.clave || ''

// Los USUARIOS de la clínica, para poder ver el sistema como una persona concreta. Es distinto de
// ver como su rol: dos personas del mismo rol ven cosas distintas si a una se le concedió o quitó un
// permiso, y sobre todo si tienen agendas distintas (lo que decide lo que ve un colaborador).
function useUsuariosPreview(enabled) {
  const { realUser } = useAuth()
  const { data } = useQuery({
    queryKey: ['usuarios-preview'],
    queryFn: () => api.get('/agentes').then(r => r.data).catch(() => []),
    staleTime: 300000,
    enabled: !!enabled,
  })
  // Fuera uno mismo (verse como uno mismo no previsualiza nada) y fuera los inactivos.
  return (data || []).filter(u => u.activo !== false && u.id !== realUser?.id)
}

function useRolesPreview(enabled) {
  const { data } = useQuery({
    queryKey: ['roles-preview'],
    queryFn: () => api.get('/permisos/rol').then(r => r.data?.rolesInfo || []).catch(() => []),
    staleTime: 300000,
    enabled: !!enabled,
  })
  // El rol admin no se previsualiza (invariante: el admin lo ve todo).
  return (data || []).filter(r => r.clave !== 'admin')
}

// Desplegable de entrada — cabecera del panel de administración. Solo lo ve quien prueba la
// aplicación (B-1000): la cuenta real del equipo de Biango, que además administra esta clínica.
export function VerComoSelector() {
  const { esAdminReal, puedeVerComo, setVerComoRol, verComoRol, setVerComoUsuario, verComoUsuario } = useAuth()
  const roles = useRolesPreview(puedeVerComo === true)
  const usuarios = useUsuariosPreview(puedeVerComo === true)
  if (!esAdminReal) return null
  if (puedeVerComo !== true) return null
  // Un solo desplegable con dos grupos: por ROL (qué ve ese perfil de serie) y por PERSONA (qué ve
  // Ana, con sus permisos y sus agendas). El valor lleva prefijo para saber cuál es cuál.
  const valor = verComoUsuario ? 'u:' + verComoUsuario.id : verComoRol ? 'r:' + verComoRol : ''
  const cambiar = (v) => {
    if (!v) { setVerComoRol(null); setVerComoUsuario(null); return }
    if (v.startsWith('u:')) {
      const u = usuarios.find(x => String(x.id) === v.slice(2))
      if (u) setVerComoUsuario({ id: u.id, rol: u.rol, nombre: [u.nombre, u.apellidos].filter(Boolean).join(' ').trim() })
    } else setVerComoRol(v.slice(2))
  }
  return (
    <select
      value={valor}
      onChange={e => cambiar(e.target.value)}
      title={t('components.VerComoRol.ver_el_sistema_como_otro', 'Ver el sistema como otro rol (previsualización)')}
      style={{
        border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)',
        borderRadius: '10px', padding: '8px 10px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer',
        maxWidth: '190px', flexShrink: 0,
      }}>
      <option value="">{t('components.VerComoRol.ver_como_soy_admin', 'Ver como… (soy admin)')}</option>
      <optgroup label={t('components.VerComoRol.por_rol', 'Por rol')}>
        {roles.map(r => <option key={r.clave} value={'r:' + r.clave}>{tx('components.VerComoRol.ver_como', 'Ver como {{nombreRol}}', { nombreRol: nombreRol(r) })}</option>)}
      </optgroup>
      {usuarios.length > 0 && (
        <optgroup label={t('components.VerComoRol.por_persona', 'Por persona')}>
          {usuarios.map(u => (
            <option key={u.id} value={'u:' + u.id}>
              {[u.nombre, u.apellidos].filter(Boolean).join(' ').trim() || u.email}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  )
}

// Barra flotante mientras se previsualiza. Global (se monta una vez, alto en el árbol) para que salga
// en cualquier pantalla; usa puedeVerComo (la cuenta REAL, no el rol previsualizado), así que nunca
// deja atrapado a quien prueba en un rol sin acceso. A quien no puede previsualizar no le sale: para
// él `verComoRol` y `verComoUsuario` ya llegan a null desde el contexto.
export function VerComoBanner() {
  const { puedeVerComo, verComoRol, setVerComoRol, verComoUsuario, setVerComoUsuario } = useAuth()
  const activo = !!verComoRol || !!verComoUsuario
  const roles = useRolesPreview(puedeVerComo === true && activo)
  if (puedeVerComo !== true || !activo) return null
  const actual = roles.find(r => r.clave === verComoRol)
  // Al ver como una PERSONA se dice su nombre y su rol: "viendo como recepción" no vale cuando lo que
  // estás comprobando es precisamente que a Ana le falta algo que sus compañeros sí tienen.
  const etiqueta = verComoUsuario
    ? `${verComoUsuario.nombre || t('components.VerComoRol.ese_usuario', 'ese usuario')} (${tDato(LABEL_SERIE[verComoUsuario.rol]) || verComoUsuario.rol})`
    : (nombreRol(actual) || verComoRol)
  return (
    <div style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      bottom: 'calc(16px + var(--sa-abajo))', zIndex: 3000,
      display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'center',
      maxWidth: 'calc((100vw / var(--z, 1)) - 24px)',
      background: '#0f2f26', color: '#fff', borderRadius: '14px', padding: '9px 14px',
      boxShadow: '0 14px 40px rgba(0,0,0,0.34)', border: '1px solid rgba(255,255,255,0.14)',
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700 }}>
        <span aria-hidden style={{ display: 'inline-flex' }}><Ico n="eye" s={15} /></span>
        {t('components.VerComoRol.viendo_como', 'Viendo como ')}<b style={{ fontWeight: 800 }}>{etiqueta}</b>
      </span>
      {/* El cambio rápido de rol solo aparece cuando se previsualiza un ROL: viendo como una persona,
          cambiar de rol desde aquí sería salir de esa persona sin decirlo. */}
      {verComoRol && (
        <select
          value={verComoRol}
          onChange={e => setVerComoRol(e.target.value || null)}
          title={t('components.VerComoRol.cambiar_de_rol', 'Cambiar de rol')}
          style={{ border: '1px solid rgba(255,255,255,0.28)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: '9px', padding: '6px 9px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>
          {roles.map(r => <option key={r.clave} value={r.clave} style={{ color: '#111' }}>{nombreRol(r)}</option>)}
        </select>
      )}
      <button
        onClick={() => { setVerComoRol(null); setVerComoUsuario(null) }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', border: 'none', background: '#fff', color: '#0f2f26', borderRadius: '9px', padding: '7px 13px', fontSize: '12.5px', fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>
        {t('components.VerComoRol.volver_a_administrador', 'Volver a administrador')}
      </button>
    </div>
  )
}

export default VerComoBanner
